import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  CurrencyDollarIcon,
  UserIcon,
  BuildingOfficeIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  TicketIcon,
  PhoneIcon,
  CreditCardIcon,
  IdentificationIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../../components/PageHeader';
import { getLocalApiUrl, authFetch } from '../../config/api';
import { useAttestationVersion } from '../../hooks/useAttestationVersion';
import {
  TIERS,
  payoutPolicyFromVenueRow,
  resolveTierFromAmount,
  formatTierRange,
  formatCurrency,
} from './payoutPolicyUtils';
import {
  KYC_DOCUMENT_CATALOG,
  KYC_DOCUMENT_GROUPS,
  optionById,
} from './kycDocumentCatalog';
import {
  INITIAL_PAYOUT_CAPTURE_FORM,
  JACKPOT_TYPES,
  PAYMENT_CASH_DESK_CAP_AUD,
  balancePaymentMethods,
  tierAllowsCash,
  tierCashLockedToFullAmount,
  effectiveMaxCashThisPayout,
  remainderIsEffectivelyZero,
  defaultCashAndBalance,
  syncBalanceFromCash,
  applyPatronResolveToForm,
  validatePayoutCapture,
  buildPayoutCapturePayload,
  parseAmount,
  sanitizeVoucherTicketNo,
  VOUCHER_TICKET_MAX_LENGTH,
} from './payoutCaptureHelpers';
import { CITIZENSHIP_COUNTRY_OPTIONS } from './citizenshipCountries';

const inputClass = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 space-y-4 shadow-sm">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 border-b border-gray-100 pb-2">
        {Icon && <Icon className="h-5 w-5 text-psg-teal shrink-0" />}
        {title}
      </h2>
      {children}
    </section>
  );
}

const cashLabel = (v) => (v ? String(v).replaceAll('_', ' ') : '—');

function newClientSubmissionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** App shell scrolls inside <main>, not window — match Layout.jsx mainScrollRef. */
function scrollAppMainToTop(behavior = 'smooth') {
  const main = document.querySelector('main.flex-1.overflow-y-auto');
  if (main) {
    main.scrollTo({ top: 0, behavior });
    return;
  }
  window.scrollTo({ top: 0, behavior });
}

export default function PayoutCapture() {
  const [venues, setVenues] = useState([]);
  const [tierLocked, setTierLocked] = useState(false);
  const [form, setForm] = useState(() => ({ ...INITIAL_PAYOUT_CAPTURE_FORM }));
  const [payoutSettings, setPayoutSettings] = useState(null);
  const [patronResolve, setPatronResolve] = useState(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveMessage, setResolveMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const cashierAttestation = useAttestationVersion('cashier_payout_confirm');
  const patronAttestation = useAttestationVersion('patron_payout_receipt');
  const [acknowledged, setAcknowledged] = useState(false);
  /** When true, cash/remainder re-default as amount, tier, or venue cash cap changes (mobile payment step). */
  const paymentSplitAutoRef = useRef(true);
  const pageTopRef = useRef(null);
  const successBannerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'));
        const j = await r.json();
        if (j.success && j.data) {
          setVenues(
            j.data.map((v) => ({
              ...v,
              policy: payoutPolicyFromVenueRow(v),
            }))
          );
        }
      } catch (_) {}
    })();
  }, []);

  const selectedVenue = useMemo(
    () => venues.find((v) => String(v.venue_id || v.id) === String(form.venue)),
    [venues, form.venue]
  );

  const tierPolicy = selectedVenue?.policy?.[form.payout_tier];
  const amountNum = parseAmount(form.amount);
  const cashAllowed = tierAllowsCash(tierPolicy?.cashDispensed, payoutSettings);
  const cashLockedToFull = tierCashLockedToFullAmount(
    tierPolicy?.cashDispensed,
    amountNum,
    payoutSettings
  );
  const balanceMethods = useMemo(
    () => balancePaymentMethods(payoutSettings),
    [payoutSettings]
  );
  const maxCash = Number.isFinite(amountNum)
    ? effectiveMaxCashThisPayout(amountNum, payoutSettings)
    : PAYMENT_CASH_DESK_CAP_AUD;
  const cashPaidNum = parseAmount(form.cash_amount_paid);
  const remainderAmount = useMemo(() => {
    if (!Number.isFinite(amountNum) || amountNum <= 0) return null;
    const cash = cashAllowed ? (Number.isFinite(cashPaidNum) ? cashPaidNum : 0) : 0;
    return Math.max(0, amountNum - cash);
  }, [amountNum, cashPaidNum, cashAllowed]);
  const showRemainderMethod =
    remainderAmount != null && !remainderIsEffectivelyZero(remainderAmount);

  useEffect(() => {
    if (!selectedVenue?.policy || tierLocked) return;
    const next =
      !Number.isFinite(amountNum) || amountNum < 0
        ? 'PV1'
        : resolveTierFromAmount(amountNum, selectedVenue.policy);
    setForm((p) => (p.payout_tier === next ? p : { ...p, payout_tier: next }));
  }, [selectedVenue, amountNum, tierLocked]);

  useEffect(() => {
    const vid = form.venue;
    if (!vid) {
      setPayoutSettings(null);
      return;
    }
    const amtQs =
      Number.isFinite(amountNum) && amountNum > 0 ? `&amount=${encodeURIComponent(amountNum)}` : '';
    (async () => {
      try {
        const r = await authFetch(
          getLocalApiUrl(`/api/payout/rules?venue_id=${encodeURIComponent(vid)}${amtQs}`)
        );
        const j = await r.json();
        if (j.success) setPayoutSettings(j.data?.settings || {});
      } catch (_) {
        setPayoutSettings(null);
      }
    })();
  }, [form.venue, amountNum]);

  useEffect(() => {
    paymentSplitAutoRef.current = true;
  }, [form.venue]);

  useEffect(() => {
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      if (paymentSplitAutoRef.current) {
        setForm((p) =>
          p.cash_amount_paid === '' && p.balance_to_pay === ''
            ? p
            : { ...p, cash_amount_paid: '', balance_to_pay: '' }
        );
      }
      return;
    }
    if (!paymentSplitAutoRef.current) return;
    const { cash, balance } = defaultCashAndBalance(amountNum, tierPolicy, payoutSettings);
    setForm((p) =>
      p.cash_amount_paid === cash && p.balance_to_pay === balance
        ? p
        : { ...p, cash_amount_paid: cash, balance_to_pay: balance }
    );
  }, [amountNum, tierPolicy, payoutSettings]);

  useEffect(() => {
    if (!showRemainderMethod) return;
    if (!balanceMethods.includes(form.payment_method)) {
      setForm((p) => ({ ...p, payment_method: balanceMethods[0] || 'EFT' }));
    }
  }, [showRemainderMethod, balanceMethods, form.payment_method]);

  const updateForm = useCallback((patch) => {
    setForm((p) => ({ ...p, ...patch }));
  }, []);

  const lookupPatron = useCallback(
    async ({ lookupType, idValue, emptyMessage }) => {
      const id = String(idValue || '').trim();
      if (!id) {
        setResolveMessage({ type: 'warn', text: emptyMessage });
        return;
      }
      const venueId = String(form.venue || '').trim();
      if (!venueId) {
        setResolveMessage({ type: 'warn', text: 'Select a venue before looking up a patron.' });
        return;
      }
      setResolveLoading(true);
      setResolveMessage(null);
      setPatronResolve(null);
      try {
        const body =
          lookupType === 'visit_id'
            ? { venue_id: venueId, visit_id: id, member_id: id, lookup_type: 'visit_id' }
            : { venue_id: venueId, member_id: id, lookup_type: 'member_id' };
        const r = await authFetch(getLocalApiUrl('/api/payout/patron/resolve'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const j = await r.json();
        if (!j.success) throw new Error(j.error || 'Lookup failed');
        setPatronResolve(j.data);
        if (j.data?.resolved) {
          setForm((prev) => applyPatronResolveToForm(prev, j.data));
          const sourceLabel = j.data.source === 'database' ? 'profile' : 'club lookup';
          setResolveMessage({
            type: 'ok',
            text: `Patron found — fields pre-filled from ${sourceLabel}.`,
          });
        } else {
          setResolveMessage({
            type: 'warn',
            text: j.data?.message || 'Patron not found — you can still enter details manually.',
          });
        }
      } catch (err) {
        setResolveMessage({ type: 'error', text: err.message || 'Lookup failed' });
      } finally {
        setResolveLoading(false);
      }
    },
    [form.venue]
  );

  const lookupMember = useCallback(() => {
    lookupPatron({
      lookupType: 'member_id',
      idValue: form.member_id,
      emptyMessage: 'Enter a member ID to look up.',
    });
  }, [form.member_id, lookupPatron]);

  const lookupVisitor = useCallback(() => {
    lookupPatron({
      lookupType: 'visit_id',
      idValue: form.visitor_id,
      emptyMessage: 'Enter an entry ID to look up.',
    });
  }, [form.visitor_id, lookupPatron]);

  const resetAfterSuccess = useCallback(() => {
    paymentSplitAutoRef.current = true;
    setForm({ ...INITIAL_PAYOUT_CAPTURE_FORM, venue: form.venue });
    setPatronResolve(null);
    setResolveMessage(null);
    setTierLocked(false);
    setAcknowledged(false);
  }, [form.venue]);

  useEffect(() => {
    if (!success) return;
    requestAnimationFrame(() => {
      scrollAppMainToTop('smooth');
      pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      successBannerRef.current?.focus?.({ preventScroll: true });
    });
  }, [success]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validationError = validatePayoutCapture({
      form,
      tierPolicy,
      patronResolve,
      settings: payoutSettings,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPayoutCapturePayload({
        form,
        venueObj: selectedVenue,
        patronResolve,
        payoutSettings,
        cashierAttestation: cashierAttestation.data,
        patronAttestation: patronAttestation.data,
        acknowledged,
        clientSubmissionId: newClientSubmissionId(),
      });

      const r = await authFetch(getLocalApiUrl('/api/monitoring/payouts'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to create payout');
      setSuccess(`Payout ${j.data?.id || ''} recorded`.trim());
      resetAfterSuccess();
    } catch (err) {
      setError(err.message || 'Failed to record payout');
    } finally {
      setSubmitting(false);
    }
  };

  const showOverseasBank = payoutSettings?.showOverseasBank !== false;
  const showPhoneRefused = payoutSettings?.showPhoneRefusedOption !== false;
  const showBankVerified = payoutSettings?.showBankVerifiedCheckbox !== false;
  const showOccupation =
    payoutSettings?.showOccupation !== false || tierPolicy?.occupationRequired;
  const occupationList = Array.isArray(payoutSettings?.occupationList)
    ? payoutSettings.occupationList.filter(Boolean)
    : [];
  const occupationUsesDropdown = occupationList.length > 0;

  return (
    <div ref={pageTopRef} className="max-w-4xl mx-auto px-4 py-6 scroll-mt-4">
      <PageHeader
        title="Payout Capture"
        subtitle="Single-page capture aligned with mobile payout fields. KYC document capture is not required on web — enter identity details manually when needed."
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">{error}</div>
        )}
        {success && (
          <div
            ref={successBannerRef}
            tabIndex={-1}
            role="status"
            className="p-3 bg-green-50 text-green-700 rounded-lg text-sm border border-green-100 outline-none"
          >
            {success}
          </div>
        )}

        <Section title="Venue & amount" icon={BuildingOfficeIcon}>
          <div>
            <label className={labelClass}>Venue *</label>
            <select
              required
              value={form.venue}
              onChange={(e) => {
                setTierLocked(false);
                updateForm({ venue: e.target.value });
              }}
              className={inputClass}
            >
              <option value="">— Select Venue —</option>
              {venues.map((v) => (
                <option key={v.id} value={v.venue_id || v.id}>
                  {v.name} ({v.code})
                </option>
              ))}
            </select>
          </div>

          {selectedVenue && tierPolicy && (
            <div className="rounded-lg border border-teal-100 bg-teal-50/80 p-3 text-sm text-gray-800">
              <div className="flex items-start gap-2 font-medium text-teal-900">
                <InformationCircleIcon className="h-5 w-5 shrink-0 text-teal-600" />
                <span>Payout policy for {form.payout_tier}</span>
              </div>
              <ul className="mt-2 space-y-1 text-xs text-gray-700 list-disc list-inside">
                <li>
                  Amount band: {formatTierRange(tierPolicy.minAmount ?? 0, tierPolicy.maxAmount)}
                  {!tierPolicy.enabled && (
                    <span className="text-amber-700 font-medium"> — tier disabled in policy</span>
                  )}
                </li>
                <li>KYC: {tierPolicy.kycRequired ? 'Required (manual text on web)' : 'Not required'}</li>
                <li>Occupation: {tierPolicy.occupationRequired ? 'Required' : 'Optional'}</li>
                <li>Cash method: {cashLabel(tierPolicy.cashDispensed)}</li>
                <li>Signatures: {tierPolicy.signaturesRequired ?? 0}</li>
              </ul>
              {tierLocked && (
                <p className="mt-2 text-xs text-amber-800">
                  Tier was set manually; change amount or venue to auto-match tier from policy again.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Amount ($) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => {
                  setTierLocked(false);
                  updateForm({ amount: e.target.value });
                }}
                placeholder="0.00"
                className={inputClass}
              />
              {selectedVenue && form.amount !== '' && Number.isFinite(amountNum) && (
                <p className="mt-1 text-xs text-gray-500">
                  Suggested tier:{' '}
                  <span className="font-medium text-gray-800">
                    {resolveTierFromAmount(amountNum, selectedVenue.policy)}
                  </span>{' '}
                  ({formatCurrency(amountNum)})
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>Payout tier</label>
              <select
                value={form.payout_tier}
                onChange={(e) => {
                  setTierLocked(true);
                  updateForm({ payout_tier: e.target.value });
                }}
                className={inputClass}
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Slip date</label>
            <input
              type="date"
              value={form.slip_date}
              onChange={(e) => updateForm({ slip_date: e.target.value })}
              className={inputClass}
            />
          </div>
        </Section>

        <Section title="Patron" icon={UserIcon}>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="patron_kind"
                checked={form.patron_kind === 'member'}
                onChange={() => updateForm({ patron_kind: 'member' })}
              />
              Member
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="patron_kind"
                checked={form.patron_kind === 'visitor'}
                onChange={() => updateForm({ patron_kind: 'visitor' })}
              />
              Visitor (Visit / Entry ID)
            </label>
          </div>

          {form.patron_kind === 'member' ? (
            <div className="flex gap-2">
              <div className="flex-1">
                <label className={labelClass}>Member #</label>
                <input
                  type="text"
                  value={form.member_id}
                  onChange={(e) => updateForm({ member_id: e.target.value })}
                  onBlur={() => {
                    if (String(form.member_id || '').trim().length >= 3) lookupMember();
                  }}
                  placeholder="Club membership number"
                  className={inputClass}
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={lookupMember}
                  disabled={resolveLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-psg-teal text-psg-teal rounded-md hover:bg-psg-teal/5 disabled:opacity-50"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  {resolveLoading ? 'Looking up…' : 'Look up'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <div className="flex-1">
                <label className={labelClass}>Visit / Entry ID</label>
                <input
                  type="text"
                  value={form.visitor_id}
                  onChange={(e) => updateForm({ visitor_id: e.target.value })}
                  onBlur={() => {
                    if (String(form.visitor_id || '').trim().length >= 3) lookupVisitor();
                  }}
                  placeholder="Enter Entry ID Number"
                  className={inputClass}
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={lookupVisitor}
                  disabled={resolveLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-psg-teal text-psg-teal rounded-md hover:bg-psg-teal/5 disabled:opacity-50"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  {resolveLoading ? 'Looking up…' : 'Look up'}
                </button>
              </div>
            </div>
          )}

          {resolveMessage && (
            <p
              className={`text-xs ${
                resolveMessage.type === 'error'
                  ? 'text-red-700'
                  : resolveMessage.type === 'ok'
                    ? 'text-green-700'
                    : 'text-amber-700'
              }`}
            >
              {resolveMessage.text}
            </p>
          )}

          {patronResolve?.on_watchlist && (
            <div
              className={`rounded-md p-3 text-sm flex gap-2 ${
                patronResolve.cdd_prohibited
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : 'bg-amber-50 border border-amber-200 text-amber-900'
              }`}
            >
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
              <div>
                <div className="font-medium">Watchlist alert</div>
                <div className="text-xs mt-0.5">
                  {patronResolve.watchlist_alert?.alert_type} — {patronResolve.watchlist_alert?.status}
                  {patronResolve.watchlist_alert?.reason
                    ? `: ${patronResolve.watchlist_alert.reason}`
                    : ''}
                </div>
                {patronResolve.cdd_prohibited && (
                  <div className="text-xs font-semibold mt-1">CDD prohibited — payout cannot be submitted.</div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>First name</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => updateForm({ first_name: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Last name</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => updateForm({ last_name: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          {showOccupation && (
            <div>
              <label className={labelClass}>
                Occupation {tierPolicy?.occupationRequired ? '*' : '(optional)'}
              </label>
              {occupationUsesDropdown ? (
                <select
                  required={tierPolicy?.occupationRequired}
                  value={form.occupation}
                  onChange={(e) => updateForm({ occupation: e.target.value })}
                  className={inputClass}
                >
                  {!tierPolicy?.occupationRequired && <option value="">— Select —</option>}
                  {tierPolicy?.occupationRequired && !form.occupation && (
                    <option value="" disabled>
                      Select occupation…
                    </option>
                  )}
                  {occupationList.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={form.occupation}
                  onChange={(e) => updateForm({ occupation: e.target.value })}
                  placeholder="e.g. Teacher"
                  className={inputClass}
                />
              )}
            </div>
          )}
        </Section>

        <Section title="Win / slip details" icon={TicketIcon}>
          <div>
            <label className={labelClass}>Jackpot type</label>
            <select
              value={form.jackpot_type}
              onChange={(e) => updateForm({ jackpot_type: e.target.value })}
              className={inputClass}
            >
              {JACKPOT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Serial / voucher ticket no.</label>
              <input
                type="text"
                value={form.voucher_ticket_no}
                maxLength={VOUCHER_TICKET_MAX_LENGTH}
                onChange={(e) =>
                  updateForm({ voucher_ticket_no: sanitizeVoucherTicketNo(e.target.value) })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Slip reference</label>
              <input
                type="text"
                value={form.slip_number}
                onChange={(e) => updateForm({ slip_number: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>EGM / game ID</label>
            <input
              type="text"
              value={form.game_id}
              onChange={(e) => updateForm({ game_id: e.target.value })}
              className={inputClass}
            />
          </div>
        </Section>

        <Section title="Contact" icon={PhoneIcon}>
          <div>
            <label className={labelClass}>Phone</label>
            <input
              type="tel"
              value={form.patron_phone}
              onChange={(e) => updateForm({ patron_phone: e.target.value })}
              className={inputClass}
            />
          </div>
          {showPhoneRefused && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.patron_phone_refused}
                onChange={(e) => updateForm({ patron_phone_refused: e.target.checked })}
                className="rounded border-gray-300"
              />
              Patron refused to provide phone
            </label>
          )}
          <div>
            <label className={labelClass}>Address</label>
            <textarea
              value={form.patron_address}
              onChange={(e) => updateForm({ patron_address: e.target.value })}
              rows={2}
              className={inputClass}
            />
          </div>
        </Section>

        <Section title="Payment" icon={CreditCardIcon}>
          {Number.isFinite(amountNum) && amountNum > 0 && (
            <p className="text-sm text-gray-700">
              Payout total: <span className="font-semibold">{formatCurrency(amountNum)}</span>
              {cashAllowed && (
                <span className="text-xs text-gray-500 ml-2">
                  (max cash at desk: {formatCurrency(maxCash)})
                </span>
              )}
            </p>
          )}

          {Number.isFinite(amountNum) && amountNum > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Cash paid at desk</label>
                <input
                  type="number"
                  min="0"
                  max={cashAllowed ? maxCash : 0}
                  step="0.01"
                  value={
                    !cashAllowed
                      ? '0'
                      : cashLockedToFull && Number.isFinite(amountNum)
                        ? String(amountNum)
                        : form.cash_amount_paid
                  }
                  disabled={!cashAllowed || cashLockedToFull}
                  onChange={(e) => {
                    paymentSplitAutoRef.current = false;
                    const synced = syncBalanceFromCash(
                      e.target.value,
                      amountNum,
                      tierPolicy,
                      payoutSettings
                    );
                    updateForm({
                      cash_amount_paid: synced.cash_amount_paid,
                      balance_to_pay: synced.balance_to_pay,
                    });
                  }}
                  className={`${inputClass} ${!cashAllowed || cashLockedToFull ? 'bg-gray-50 text-gray-600' : ''}`}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {!cashAllowed
                    ? 'This tier pays the full amount via EFT or cheque — no cash at desk.'
                    : cashLockedToFull
                      ? 'Cash only tier — full amount paid at desk (within venue cash limit).'
                      : `Adjust cash only (up to ${formatCurrency(maxCash)}). Remainder is paid via EFT or cheque.`}
                </p>
              </div>
              <div>
                <label className={labelClass}>Remainder</label>
                <div
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-900 font-semibold tabular-nums"
                  aria-live="polite"
                >
                  {remainderAmount != null ? formatCurrency(remainderAmount) : '—'}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Payout total minus cash paid
                  {remainderAmount != null && remainderAmount > 0.005
                    ? ` — pay via ${balanceMethods.length ? balanceMethods.join(' or ') : 'EFT/cheque'} below`
                    : ''}
                  .
                </p>
              </div>
            </div>
          )}

          {showRemainderMethod && balanceMethods.length > 0 && (
            <div>
              <label className={labelClass}>Pay remainder via</label>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Pay remainder via">
                {balanceMethods.map((m) => {
                  const active = form.payment_method === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => updateForm({ payment_method: m })}
                      className={`flex-1 min-w-[7rem] px-3 py-2.5 rounded-lg text-sm font-semibold border transition ${
                        active
                          ? 'bg-psg-teal/10 text-psg-teal border-psg-teal'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {showRemainderMethod && balanceMethods.length === 0 && (
            <p className="text-sm text-red-700">
              There is a remainder after cash (venue max cash {formatCurrency(maxCash)}), but EFT and
              cheque are disabled for this venue. Enable at least one in payout settings.
            </p>
          )}

          {(form.payment_method === 'EFT' && showRemainderMethod) && (
            <div className="space-y-3 border-t border-gray-100 pt-3">
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={form.eft_region === 'australian'}
                    onChange={() => updateForm({ eft_region: 'australian' })}
                  />
                  Australian bank
                </label>
                {showOverseasBank && (
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={form.eft_region === 'overseas'}
                      onChange={() => updateForm({ eft_region: 'overseas' })}
                    />
                    Overseas bank
                  </label>
                )}
              </div>
              {form.eft_region === 'australian' ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>Bank name</label>
                    <input
                      type="text"
                      value={form.eft_bank_name}
                      onChange={(e) => updateForm({ eft_bank_name: e.target.value })}
                      placeholder="e.g. Commonwealth Bank"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>BSB *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.eft_bsb}
                      onChange={(e) => updateForm({ eft_bsb: e.target.value })}
                      placeholder="XXX-XXX"
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Account number *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.eft_account_number}
                      onChange={(e) => updateForm({ eft_account_number: e.target.value })}
                      placeholder="Digits only"
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    ['eft_os_account_name', 'Account name *', 'Name on account'],
                    ['eft_os_bank_name', 'Bank name *', 'Receiving bank'],
                    ['eft_os_bsb', 'BSB / routing', 'XXX-XXX'],
                    ['eft_os_account_no', 'Account number *', 'Account'],
                    ['eft_os_swift', 'SWIFT / BIC', 'e.g. CTBAAU2S'],
                    ['eft_os_branch_name', 'Branch name', 'Branch name'],
                    ['eft_os_contact_email', 'Contact email', 'email'],
                    ['eft_os_contact_phone', 'Contact phone', 'phone'],
                  ].map(([key, lbl, placeholder]) => (
                    <div key={key}>
                      <label className={labelClass}>{lbl}</label>
                      <input
                        type="text"
                        value={form[key]}
                        onChange={(e) => updateForm({ [key]: e.target.value })}
                        placeholder={placeholder}
                        className={inputClass}
                      />
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Branch address</label>
                    <input
                      type="text"
                      value={form.eft_os_branch_address}
                      onChange={(e) => updateForm({ eft_os_branch_address: e.target.value })}
                      placeholder="Full branch address"
                      className={inputClass}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Residential address</label>
                    <input
                      type="text"
                      value={form.eft_os_residential_address}
                      onChange={(e) => updateForm({ eft_os_residential_address: e.target.value })}
                      placeholder="Patron's residential address"
                      className={inputClass}
                    />
                  </div>
                </div>
              )}
              {showBankVerified && (
                <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.bank_account_physically_verified}
                    onChange={(e) => updateForm({ bank_account_physically_verified: e.target.checked })}
                    className="rounded border-gray-300 mt-0.5"
                  />
                  <span>
                    <span className="font-medium text-gray-900">Bank details verified in person</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Turn on if account details were sighted or verified at the counter.
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}

          {(form.payment_method === 'Cheque' && showRemainderMethod) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-gray-100 pt-3">
              <div>
                <label className={labelClass}>Name on cheque *</label>
                <input
                  type="text"
                  value={form.cheque_name_on}
                  onChange={(e) => updateForm({ cheque_name_on: e.target.value })}
                  placeholder="Defaults to patron name"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Cheque date</label>
                <input
                  type="date"
                  value={form.cheque_date}
                  onChange={(e) => updateForm({ cheque_date: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
          )}
        </Section>

        <Section title="Identity (optional — no document upload on web)" icon={IdentificationIcon}>
          <p className="text-xs text-gray-500 mb-3">
            Select document type (same catalog as mobile). Capture images on a tablet when needed; enter missing
            fields here for KYC completeness.
          </p>
          <div className="space-y-4 mb-4">
            {KYC_DOCUMENT_GROUPS.map((g) => (
              <div key={g.id}>
                <div className="text-xs font-semibold text-gray-600 mb-1.5">{g.title}</div>
                <div className="flex flex-wrap gap-2">
                  {KYC_DOCUMENT_CATALOG.filter((o) => o.group === g.id).map((o) => {
                    const active = form.document_option === o.id
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() =>
                          updateForm({
                            document_option: o.id,
                            document_type: o.documentType === 'none' ? '' : o.documentType,
                            kyc_mode: o.id === 'no_document' ? 'skipped' : 'assisted',
                            kyc_source: o.id === 'no_document' ? 'none' : 'profilesafe_ocr',
                          })
                        }
                        className={`px-3 py-1.5 text-xs font-medium rounded border ${
                          active
                            ? 'bg-psg-teal text-white border-psg-teal'
                            : 'bg-white text-gray-800 border-gray-300 hover:border-psg-teal/50'
                        }`}
                      >
                        {o.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
          {form.document_option && optionById(form.document_option)?.id === 'intl_passport' ? (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mb-3">
              International passport has no Australian address — enter a local residential address below.
            </p>
          ) : null}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Document number</label>
              <input
                type="text"
                value={form.document_number}
                onChange={(e) => updateForm({ document_number: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Card number</label>
              <input
                type="text"
                value={form.document_card_number}
                onChange={(e) => updateForm({ document_card_number: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Date of birth</label>
              <input
                type="date"
                value={form.date_of_birth}
                onChange={(e) => updateForm({ date_of_birth: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Expiry</label>
              <input
                type="date"
                value={form.expiry_date}
                onChange={(e) => updateForm({ expiry_date: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>State of issue</label>
              <input
                type="text"
                value={form.state_of_issue}
                onChange={(e) => updateForm({ state_of_issue: e.target.value })}
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>
                Citizenship
                {payoutSettings?.requireCitizenshipDeclaration ? (
                  <span className="text-red-600"> *</span>
                ) : null}
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {[
                  { id: 'single', label: 'Single' },
                  { id: 'multiple', label: 'More than one' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      const countries =
                        opt.id === 'single'
                          ? (form.citizenship_countries || []).slice(0, 1)
                          : form.citizenship_countries || []
                      updateForm({
                        citizenship_mode: opt.id,
                        citizenship_countries: countries,
                        citizenship: countries.filter(Boolean).join(', '),
                      })
                    }}
                    className={`px-3 py-1.5 text-sm rounded-md border ${
                      form.citizenship_mode === opt.id
                        ? 'border-psg-teal bg-psg-teal/10 text-psg-teal font-medium'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {form.citizenship_mode === 'single' && (
                <select
                  value={(form.citizenship_countries || [])[0] || ''}
                  onChange={(e) => {
                    const v = e.target.value
                    const countries = v ? [v] : []
                    updateForm({
                      citizenship_countries: countries,
                      citizenship: countries.join(', '),
                    })
                  }}
                  className={inputClass}
                >
                  <option value="">Select country…</option>
                  {CITIZENSHIP_COUNTRY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              )}
              {form.citizenship_mode === 'multiple' && (
                <div className="space-y-2">
                  {[0, 1, 2].map((idx) => (
                    <select
                      key={idx}
                      value={(form.citizenship_countries || [])[idx] || ''}
                      onChange={(e) => {
                        const next = [...(form.citizenship_countries || [])]
                        while (next.length <= idx) next.push('')
                        next[idx] = e.target.value
                        const countries = next.filter(Boolean)
                        updateForm({
                          citizenship_countries: countries,
                          citizenship: countries.join(', '),
                        })
                      }}
                      className={inputClass}
                    >
                      <option value="">{idx === 0 ? 'First country…' : idx === 1 ? 'Second country…' : 'Additional country (optional)…'}</option>
                      {CITIZENSHIP_COUNTRY_OPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  ))}
                  <p className="text-xs text-gray-500">Select at least two countries of citizenship.</p>
                </div>
              )}
              {!form.citizenship_mode && (
                <input
                  type="text"
                  value={form.citizenship}
                  onChange={(e) => updateForm({ citizenship: e.target.value })}
                  placeholder="Or type citizenship (legacy)"
                  className={inputClass}
                />
              )}
            </div>
          </div>
        </Section>

        <Section title="Notes" icon={CurrencyDollarIcon}>
          <textarea
            value={form.notes}
            onChange={(e) => updateForm({ notes: e.target.value })}
            rows={2}
            placeholder="Optional cashier notes"
            className={inputClass}
          />
        </Section>

        <div className="rounded-lg border border-psg-teal/30 bg-psg-teal/5 p-4">
          <div className="flex items-start gap-2 mb-2">
            <ShieldCheckIcon className="w-5 h-5 text-psg-teal shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-psg-teal">Cashier attestation</div>
              {cashierAttestation.loading && (
                <div className="text-xs text-gray-500 mt-1">Loading active wording…</div>
              )}
              {cashierAttestation.error && (
                <div className="text-xs text-red-600 mt-1">
                  {cashierAttestation.error}. Payout can still be recorded but no attestation version will be stamped.
                </div>
              )}
              {!cashierAttestation.loading && !cashierAttestation.error && !cashierAttestation.data && (
                <div className="text-xs text-amber-700 mt-1">
                  No active cashier attestation version configured for this tenant.
                </div>
              )}
              {cashierAttestation.data && (
                <>
                  <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap leading-relaxed">
                    {cashierAttestation.data.body_text}
                  </p>
                  <div className="text-[11px] text-gray-500 mt-2 font-mono">
                    Version <span className="font-semibold">{cashierAttestation.data.id}</span>
                    {cashierAttestation.data.version_label
                      ? ` (${cashierAttestation.data.version_label})`
                      : ''}
                  </div>
                </>
              )}
            </div>
          </div>
          {patronAttestation.data && (
            <label className="flex items-start gap-2 mt-3 pt-3 border-t border-psg-teal/20 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1 rounded border-gray-300"
              />
              <span className="text-xs text-gray-600 leading-relaxed">
                <b>Patron acknowledgement captured.</b> Check only if the patron has acknowledged:
                <span className="block italic text-gray-500 mt-1">"{patronAttestation.data.body_text}"</span>
              </span>
            </label>
          )}
        </div>

        <div className="flex justify-end pt-2 pb-8">
          <button
            type="submit"
            disabled={submitting || patronResolve?.cdd_prohibited}
            className="px-6 py-2.5 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-600 disabled:opacity-50 font-medium"
          >
            {submitting ? 'Recording…' : 'Record Payout'}
          </button>
        </div>
      </form>
    </div>
  );
}
